import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

export interface SentryReactOptions {
  /** Sentry/GlitchTip DSN. If empty/undefined, Sentry is disabled. */
  dsn?: string;

  /** Environment name: 'staging' | 'production' | 'development' */
  environment?: string;

  /** Customer/tenant identifier (e.g. 'dzbank', 'mhb'). Set as a global 'customer' tag on all events. */
  customer?: string;

  /** Release version (e.g. git SHA or semver) */
  release?: string;

  /** Sample rate for performance tracing: 0.0 to 1.0 */
  tracesSampleRate?: number;

  /** Enable debug mode */
  debug?: boolean;
}

/**
 * Initialize Sentry for React with GlitchTip-compatible settings.
 *
 * Call this BEFORE React renders (in main.tsx, before createRoot).
 *
 * ```ts
 * import { initSentry } from '@monorepo/react/sentry';
 *
 * initSentry({
 *   dsn: process.env.NX_SENTRY_DSN,
 *   environment: process.env.NX_SENTRY_ENVIRONMENT || 'development',
 * });
 * ```
 */
export function initSentry(options: SentryReactOptions): void {
  if (!options.dsn) {
    console.warn('[Sentry] DSN not configured. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment || 'development',
    release: options.release,
    tracesSampleRate: options.tracesSampleRate ?? 0.2,
    debug: options.debug ?? false,
    integrations: [new BrowserTracing()],
    // Scrub sensitive data
    beforeSend(event) {
      // Remove cookies from request data
      if (event.request?.cookies) {
        delete event.request.cookies;
      }
      return event;
    },
    // Filter out common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Network errors
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // ResizeObserver (benign browser quirk)
      'ResizeObserver loop',
    ],
  });

  // Set customer tag globally so every event is tagged
  if (options.customer) {
    Sentry.setTag('customer', options.customer);
  }

  console.log(
    `[Sentry] Initialized [env=${options.environment}]` +
      (options.customer ? ` [customer=${options.customer}]` : ''),
  );
}
